import React, { useState, useCallback, useEffect } from 'react';
import { FormControlLabel } from '@mui/material';
import {
  Button,
  Drawer,
  FormField,
  Input,
  Select,
} from '@fashionnovateam/nova-ui-toolkit';
import validateEmail from '../../Lib/Utils/Helpers/validate-email';
import normalizeEmail from '../../Lib/Utils/Helpers/normalize-email';
import { countriesPhoneCode } from '../../Lib/Constants/countries-phone-code';
import Roles from '../../Lib/Constants/roles';
import CustomSwitch from '../ReusableComponents/Switch';
import { User } from '../../Lib/Api/vendor';

type NewMemberDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: ContactData) => Promise<void>;
  existingContacts: ContactData[];
  canCreateAdmin: boolean;
  contactToEdit?: ContactData | null;
  loading: boolean;
  filters: string;
  user?: User;
};

type ContactData = {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  title: string;
  phone_country: string;
  phone: string;
  feature_permissions?: {
    allow_printing?: boolean;
  };
};

type ErrorState = {
  [K in keyof ContactData]?: string;
};

const defaultFormValues: ContactData = {
  first_name: '',
  last_name: '',
  email: '',
  role: Roles.VENDOR_STAFF,
  title: '',
  phone_country: 'US',
  phone: '',
  feature_permissions: {
    allow_printing: false,
  },
};

export function NewMemberDrawer({
  isOpen,
  onClose,
  onSave,
  existingContacts,
  contactToEdit,
  loading,
  canCreateAdmin,
  user,
}: NewMemberDrawerProps) {
  const [formValues, setFormValues] = useState<ContactData>(defaultFormValues);
  const [errors, setErrors] = useState<ErrorState>({});

  useEffect(() => {
    setFormValues({
      ...defaultFormValues,
      ...(contactToEdit || {}),
      feature_permissions: {
        allow_printing:
          contactToEdit?.feature_permissions?.allow_printing ?? false,
      },
      ...(!canCreateAdmin ? { role: Roles.VENDOR_STAFF } : {}),
    });
  }, [contactToEdit, isOpen, canCreateAdmin]);

  const closeDrawer = () => {
    setFormValues(defaultFormValues);
    setErrors({});
    onClose();
  };

  const handlePrintingPermissionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFormValues((prev) => ({
      ...prev,
      feature_permissions: {
        ...prev.feature_permissions,
        allow_printing: event.target.checked,
      },
    }));
  };

  const handleChange = useCallback(
    (name: keyof ContactData) =>
      (
        event:
          | React.ChangeEvent<HTMLInputElement>
          | React.ChangeEvent<HTMLSelectElement>,
      ) => {
        const { value } = event.target;
        setFormValues((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      },
    [],
  );

  const validateUniqueEmail = useCallback(
    (email: string) =>
      !existingContacts.some(
        (c) => normalizeEmail(c.email) === normalizeEmail(email),
      ),
    [existingContacts],
  );

  const validateContact = useCallback((): ErrorState => {
    const newErrors: ErrorState = {};
    if (!formValues.first_name) newErrors.first_name = 'First name is required';
    if (!formValues.last_name) newErrors.last_name = 'Last name is required';
    if (!formValues.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formValues.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (!contactToEdit && !validateUniqueEmail(formValues.email)) {
      newErrors.email = 'This email is already in use';
    }
    const phoneRegex =
      /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/;
    if (formValues.phone && !phoneRegex.test(formValues.phone)) {
      newErrors.phone = 'Phone number must contain only digits';
    }
    return newErrors;
  }, [formValues, validateUniqueEmail, contactToEdit]);

  const handleSubmit = async () => {
    const validationErrors = validateContact();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      await onSave(formValues);
      setFormValues(defaultFormValues);
    }
  };

  return (
    <Drawer width="520px" isOpen={isOpen} onClose={closeDrawer}>
      <Drawer.Header>
        <Drawer.Title>
          {contactToEdit ? 'Edit Team Member' : 'Invite New Team Member'}
        </Drawer.Title>
        <Drawer.Description className="mt-2">
          We&apos;ll send an email to the new member with instructions to
          activate their account and login.
        </Drawer.Description>
      </Drawer.Header>
      <Drawer.Content>
        <div className="flex flex-col gap-4">
          <div className="flex gap-4 w-full">
            <FormField
              error={errors.first_name}
              className="flex-1"
              label="First Name"
              inputId="firstName"
              required
            >
              <Input
                id="firstName"
                value={formValues.first_name || ''}
                onChange={handleChange('first_name')}
                required
              />
            </FormField>
            <FormField
              error={errors.last_name}
              className="flex-1"
              label="Last Name"
              inputId="lastName"
              required
            >
              <Input
                id="lastName"
                value={formValues.last_name || ''}
                onChange={handleChange('last_name')}
                required
              />
            </FormField>
          </div>
          <FormField
            error={errors.email}
            label="Email"
            inputId="email"
            required
          >
            <Input
              id="email"
              type="email"
              value={formValues.email || ''}
              onChange={handleChange('email')}
              required
            />
          </FormField>
          <div className="flex gap-4 w-full items-center">
            <FormField
              error={errors.phone_country}
              className="flex-shrink"
              label="Phone Number"
              inputId="phoneNumber"
            >
              <Select
                value={formValues.phone_country}
                onChange={
                  handleChange(
                    'phone_country',
                  ) as React.ChangeEventHandler<HTMLSelectElement>
                }
                options={countriesPhoneCode.map((countryCode) => ({
                  value: countryCode.code,
                  label: `${countryCode.code} (${countryCode.dial_code})`,
                }))}
              />
            </FormField>
            <FormField
              error={errors.phone}
              className="flex-1"
              label="Phone Number"
              inputId="phoneNumber"
            >
              <Input
                id="phone"
                value={formValues.phone || ''}
                onChange={handleChange('phone')}
                required
              />
            </FormField>
          </div>

          <FormField label="Title" inputId="title">
            <Input
              id="title"
              value={formValues.title || ''}
              onChange={handleChange('title')}
            />
          </FormField>
          <FormField error={errors.role} label="Role" inputId="role" required>
            <Select
              value={formValues.role}
              onChange={
                handleChange(
                  'role',
                ) as React.ChangeEventHandler<HTMLSelectElement>
              }
              options={[
                {
                  value: Roles.VENDOR_STAFF,
                  label: 'Staff',
                },
                {
                  value: Roles.VENDOR_OPERATIONS,
                  label: 'Operations',
                },
                ...(canCreateAdmin
                  ? [
                      {
                        value: Roles.VENDOR,
                        label: 'Admin',
                      },
                    ]
                  : []),
              ]}
            />
          </FormField>
          {user?.feature_permissions?.allow_printing && (
            <FormControlLabel
              control={
                <CustomSwitch
                  checked={formValues.feature_permissions?.allow_printing}
                  className="ml-2"
                  onChange={handlePrintingPermissionChange}
                />
              }
              label={
                <span className="text-[13px] leading-[16px] font-medium font-geist">
                  Allow Printing for Team Member
                </span>
              }
              className="flex items-center space-x-3 font-bold font-geist"
            />
          )}
        </div>
      </Drawer.Content>
      <Drawer.Footer>
        <Button color="secondary" onClick={closeDrawer}>
          Cancel
        </Button>
        <Button loading={loading} onClick={handleSubmit}>
          {contactToEdit ? 'Save Changes' : 'Invite'}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
}
